import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../../config/axiosInterceptor";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { value } from "../../config/data";

const statusMap = {
    0: "Pending",
    1: "Approved",
    2: "Rejected"
};

const AdminBillView = ({ bill, invoiceForType, onBack, refreshList }) => {
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");
    const [invoiceData, setInvoiceData] = useState([]);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [invoiceError, setInvoiceError] = useState("");
    const billRef = useRef();

    // Fetch invoice data for this bill
    useEffect(() => {
        const fetchInvoice = async () => {
            setInvoiceLoading(true);
            setInvoiceError("");
            try {
                const res = await axiosInstance.get(
                    `${process.env.REACT_APP_BASEURL1}/admin/withdrawal/InvoiceForBd`,
                    {
                        params: {
                            invoiceFor: bill.uniqueId,
                            invoiceForType: invoiceForType,
                            start: bill.startDate,
                            end: bill.endDate
                        }
                    }
                );
                if (res.data.success) {
                    setInvoiceData(res.data.details || []);
                } else {
                    setInvoiceError(res.data.message || "Failed to fetch invoice data");
                }
            } catch (err) {
                setInvoiceError(err.message || "Error fetching invoice data");
            }
            setInvoiceLoading(false);
        };
        fetchInvoice();
    }, [bill, invoiceForType]);

    // Overlay PAID stamp
    const PaidStamp = () => (
        <div
            style={{
                position: "absolute",
                top: "40%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(-20deg)",
                fontSize: 80,
                color: "rgba(0,128,0,0.25)",
                fontWeight: "bold",
                pointerEvents: "none",
                userSelect: "none",
                zIndex: 10,
                textShadow: "2px 2px 8px #fff"
            }}
        >
            PAID
        </div>
    );

    // Totals
    const hostAmount = invoiceData.reduce(
        (sum, item) =>
            sum +
            parseFloat(
                ((item?.totalDollor || 0) * value.USDINR).toFixed(2)
            ),
        0
    );
    const agComm = invoiceData.reduce(
        (sum, item) =>
            sum +
            parseFloat(
                (
                    ((item?.totalDollor || 0) * value.USDINR) *
                    value.agencyComm
                ).toFixed(2)
            ),
        0
    );
    const total = hostAmount + agComm;

   const handleApprove = async () => {
        setMsg("");
        setLoading(true);
        try {
            const billNode = billRef.current;
            // Add PAID stamp
            const stamp = document.createElement("div");
            stamp.innerHTML = "PAID";
            Object.assign(stamp.style, {
                position: "absolute",
                top: "40%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(-20deg)",
                fontSize: "80px",
                color: "rgba(0,128,0,0.25)",
                fontWeight: "bold",
                pointerEvents: "none",
                userSelect: "none",
                zIndex: 10,
                textShadow: "2px 2px 8px #fff"
            });
            billNode.appendChild(stamp);

            // Convert to canvas
            const canvas = await html2canvas(billNode, { scale: 2 });
            billNode.removeChild(stamp);

            // Convert to PDF
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "pt", "a4");
            const pageWidth = pdf.internal.pageSize.getWidth();
            const imgHeight = (canvas.height * pageWidth) / canvas.width;
            pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);

            // Convert PDF to blob
            const pdfBlob = pdf.output("blob");
            const formData = new FormData();
            formData.append("id", bill._id);
            formData.append("invoiceForType", invoiceForType);
            formData.append("pdf", pdfBlob, "paid_bill.pdf");

            const res = await axiosInstance.post(
                `${process.env.REACT_APP_BASEURL1}/admin/withdrawal/approveBill`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            if (res.data.success) {
                setMsg("Bill approved and PDF uploaded!");
                refreshList && refreshList();
                onBack();
            } else {
                setMsg(res.data.message || "Failed to approve bill.");
            }
        } catch (e) {
            setMsg("Failed to approve bill.");
        }
        setLoading(false);
    };

    const handleReject = async () => {
        setLoading(true);
        setMsg("");
        try {
            const res = await axiosInstance.post(
                `${process.env.REACT_APP_BASEURL1}/admin/withdrawal/rejectBill`,
                { id: bill._id, invoiceForType }
            );
            if (res.data.success) {
                setMsg("Bill rejected!");
                refreshList && refreshList();
                onBack();
            } else {
                setMsg(res.data.message || "Failed to reject bill.");
            }
        } catch (e) {
            setMsg("Failed to reject bill.");
        }
        setLoading(false);
    };

    // Helper
    const formatDate = (date) =>
        date
            ? new Date(date).toLocaleDateString("en-GB", {
                year: "numeric",
                month: "short",
                day: "2-digit",
            })
            : "-";

    // Bill view design (same as InvoiceForBd, but with ref and absolute for stamp)
    return (
        <div style={{ position: "relative" }}>
            <div
                ref={billRef}
                style={{
                    maxWidth: 900,
                    margin: "40px auto",
                    padding: 32,
                    background: "#fff",
                    border: "2px solid #222",
                    borderRadius: 12,
                    fontFamily: "serif",
                    boxShadow: "0 4px 24px #0002",
                    position: "relative",
                    minHeight: 600,
                }}
            >
                {bill.approved === 1 && <PaidStamp />}
                <div style={{ textAlign: "center", marginBottom: 8 }}>
                    <img
                        src="/mastiLive_logo.png"
                        alt="Company Logo"
                        style={{ height: 60, marginBottom: 8 }}
                    />
                    <h1 style={{ margin: 0, fontWeight: 700, letterSpacing: 2 }}>
                        MastiShorts Private Limited
                    </h1>
                    <div style={{ fontSize: 18, fontWeight: 600, margin: "8px 0 0 0" }}>
                        Computer Generated Bill
                    </div>
                    <div style={{ fontSize: 14, color: "#555" }}>
                        <b>Period:</b> {formatDate(bill.startDate)} to {formatDate(bill.endDate)}
                    </div>
                </div>
                <hr style={{ margin: "18px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                    <div>
                        <b>BD Unique ID:</b> {bill.bd}
                    </div>
                    <div>
                        <b>Date:</b> {formatDate(bill.createdAt)}
                    </div>
                </div>
                <div
                    style={{
                        background: "#f7f7f7",
                        border: "1px solid #ddd",
                        padding: 16,
                        borderRadius: 8,
                        marginBottom: 18,
                        fontSize: 20,
                        fontWeight: 700,
                        textAlign: "center",
                        letterSpacing: 1,
                    }}
                >
                    Total Amount (INR): ₹{total.toLocaleString("en-IN")}
                </div>
                <div style={{ marginBottom: 18, display: "flex", gap: 32, fontSize: 16 }}>
                    <div>
                        <b>Host Amount:</b> ₹{hostAmount.toLocaleString("en-IN")}
                    </div>
                    <div>
                        <b>Agency Commission:</b> ₹{agComm.toLocaleString("en-IN")}
                    </div>
                    <div>
                        <b>Total:</b> ₹{total.toLocaleString("en-IN")}
                    </div>
                </div>
                {invoiceLoading && <p>Loading invoice data...</p>}
                {invoiceError && <p style={{ color: "red" }}>{invoiceError}</p>}
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 24, fontSize: 15 }}>
                    <thead>
                        <tr style={{ background: "#f5f5f5" }}>
                            <th style={{ border: "1px solid #ccc", padding: 8 }}>#</th>
                            <th style={{ border: "1px solid #ccc", padding: 8 }}>Agency</th>
                            <th style={{ border: "1px solid #ccc", padding: 8 }}>Id</th>
                            <th style={{ border: "1px solid #ccc", padding: 8 }}>Coins</th>
                            <th style={{ border: "1px solid #ccc", padding: 8 }}>Amount(USD)</th>
                            <th style={{ border: "1px solid #ccc", padding: 8 }}>Amount(INR)</th>
                            <th style={{ border: "1px solid #ccc", padding: 8 }}>Ag Comm(10%)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoiceData.length === 0 && !invoiceLoading ? (
                            <tr>
                                <td colSpan={7} style={{ textAlign: "center", padding: 16 }}>
                                    No records found
                                </td>
                            </tr>
                        ) : (
                            invoiceData.map((item, idx) => {
                                const inr = parseFloat(
                                    ((item?.totalDollor || 0) * value.USDINR).toFixed(2)
                                );
                                const agc = parseFloat((inr * value.agencyComm).toFixed(2));
                                return (
                                    <tr key={item._id || idx}>
                                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{idx + 1}</td>
                                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{item.agencyName || "-"}</td>
                                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{item.agencyId || "-"}</td>
                                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{item.totalAmount || "-"}</td>
                                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{item.totalDollor?.toFixed(2) || "-"}</td>
                                        <td style={{ border: "1px solid #ccc", padding: 8 }}>
                                            <span style={{ fontWeight: "bold" }}>Rs </span>
                                            {inr.toLocaleString("en-IN")}
                                        </td>
                                        <td style={{ border: "1px solid #ccc", padding: 8 }}>
                                            <span style={{ fontWeight: "bold" }}>Rs </span>
                                            {agc.toLocaleString("en-IN")}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>


            </div>
            <div style={{ marginBottom: 18, display: "flex", gap: 32, fontSize: 16 }}>
                <div>
                    <b>Paid Bill PDF:</b>{" "}
                    {bill.billPath ? (
                        <a
                            href={
                                bill.billPath.startsWith("http")
                                    ? bill.billPath
                                    : `${process.env.REACT_APP_BASEURL1.replace(/\/$/, "")}/${bill.billPath.replace(/^\/?/, "")}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View PDF
                        </a>
                    ) : (
                        "Not uploaded"
                    )}
                </div>
            </div>
            {msg && <div style={{ color: "#06690d", marginBottom: 12, textAlign: "center" }}>{msg}</div>}
            {bill.approved !== 1 && (
                <div style={{ textAlign: "center", margin: "24px 0" }}>
                    <div
                        style={{
                            background: "#f9f9f9",
                            border: "1px dashed #888",
                            padding: 16,
                            borderRadius: 8,
                            marginBottom: 18,
                            color: "#444",
                            fontSize: 16,
                            textAlign: "center",
                        }}
                    >
                        <b>This is a computer generated bill.</b> <br />
                        <span style={{ color: "#c72a16" }}>This is unpaid bill.</span><br />
                        Once approved, you will get downloadable paid bill.
                    </div>
                    <button
                        onClick={handleApprove}
                        disabled={loading}
                        style={{
                            marginRight: 16,
                            background: "#06690d",
                            color: "#fff",
                            padding: "10px 32px",
                            border: "none",
                            borderRadius: 6,
                            fontWeight: 600,
                            letterSpacing: 1,
                            fontSize: 18,
                            cursor: loading ? "not-allowed" : "pointer"
                        }}
                    >
                        Approve & Upload Paid Bill
                    </button>
                    <button
                        onClick={handleReject}
                        disabled={loading}
                        style={{
                            background: "#c72a16",
                            color: "#fff",
                            padding: "10px 32px",
                            border: "none",
                            borderRadius: 6,
                            fontWeight: 600,
                            letterSpacing: 1,
                            fontSize: 18,
                            cursor: loading ? "not-allowed" : "pointer"
                        }}
                    >
                        Reject
                    </button>
                </div>
            )}
            <div style={{ textAlign: "center" }}>
                <button onClick={onBack} style={{ marginTop: 12 }}>Back</button>
            </div>
        </div>
    );
};

export default AdminBillView;